import { useEffect, useState } from 'react';
import { BsFilePdf } from 'react-icons/bs';
import { useMediaQuery } from 'react-responsive';
import { Spinner } from '../../../../../shared/ui/Spinners';
import { Tab, TabItem } from '../../../../../shared/ui/tab';
import { ImageSlider } from '../../../../../shared/media/slider';
import { Panel } from '../../../../../shared/ui/panel/panels';
import {
    getProductsGuides,
    getProductsImages,
} from '../../../../../services/products/productService';
import { usePortalStore } from '../../../../../stores/portalStore';
import { downloadPDF, getPdfDownloadUrl } from '../../../../../utils/documents';
import { getBase64Img } from '../../../../../utils/format';
import { getLastFromSplit } from '../../../../../utils/text';

const GREEN = '#318300';
const GREEN_LIGHT = '#f0f7eb';

const TABS = [
    { id: 0, label: 'Descrição' },
    { id: 1, label: 'Especificações' },
    { id: 2, label: 'Arquivos' },
];

// ─── Hook interno: busca imagem do produto ─────────────────────────────────────
const useProductImage = (sapCode) => {
    const [image, setImage] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            setLoading(true);
            const img = await getProductsImages([sapCode]);
            setImage([...img]);
            setLoading(false);
        })();
    }, [sapCode]);

    return { image, loading };
};

// ─── Utilitário: escolhe o melhor texto de explicação ─────────────────────────
const resolveExplanation = (subCategory) => {
    if (subCategory?.explanation?.trim()) return subCategory.explanation;
    if (subCategory?.explanation1?.trim()) return subCategory.explanation1;
    return '';
};

// ─── Badge de categoria ────────────────────────────────────────────────────────
const CategoryBadge = ({ label }) => {
    if (!label) return null;
    return (
        <span
            style={{
                display: 'inline-block',
                marginBottom: '8px',
                padding: '2px 10px',
                borderRadius: '20px',
                fontSize: '11px',
                fontWeight: '600',
                letterSpacing: '0.4px',
                backgroundColor: GREEN_LIGHT,
                color: GREEN,
            }}
        >
            {label}
        </span>
    );
};

// ─── Bloco de imagem contida ───────────────────────────────────────────────────
const ProductImageBox = ({ image, loading, mobile = false }) => (
    <div
        style={{
            width: mobile ? '100%' : '200px',
            maxWidth: '100%',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '180px',
            marginBottom: mobile ? '16px' : '0',
            padding: mobile ? '10px 8px 16px' : '12px 8px',
            boxSizing: 'border-box',
            overflow: 'hidden',
        }}
    >
        {loading ? (
            <Spinner />
        ) : (
            <ImageSlider
                images={image.map(getBase64Img)}
                style={{ maxHeight: '180px', maxWidth: '100%', objectFit: 'contain' }}
            />
        )}
    </div>
);

// ─── Aba: Descrição ────────────────────────────────────────────────────────────
const EquipmentDescription = ({ subCategory }) => {
    const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1224px)' });
    const currentUser = usePortalStore((state) => state.currentUser);
    const { image, loading } = useProductImage(subCategory.selectedModel);
    const explanation = resolveExplanation(subCategory);

    const textBlock = (
        <div
            style={{
                flex: '1 1 260px',
                minWidth: isTabletOrMobile ? 0 : '220px',
                paddingLeft: isTabletOrMobile ? 0 : '4px',
            }}
        >
            <CategoryBadge label={subCategory.product_category?.description} />
            <h3
                style={{
                    margin: '0 0 10px',
                    fontSize: '15px',
                    fontWeight: '700',
                    color: '#222',
                    lineHeight: '1.4',
                    overflowWrap: 'anywhere',
                }}
            >
                {subCategory.description}
                {currentUser.canSeeFullOrdersTable && (
                    <span
                        style={{
                            fontSize: '12px',
                            color: '#999',
                            marginLeft: '8px',
                            fontWeight: '400',
                        }}
                    >
                        {subCategory.selectedModel}
                    </span>
                )}
            </h3>
            {explanation ? (
                <p
                    style={{
                        margin: 0,
                        fontSize: '13px',
                        color: '#444',
                        lineHeight: '1.7',
                        whiteSpace: 'pre-line',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                        maxHeight: isTabletOrMobile ? 'none' : '340px',
                        overflowY: 'auto',
                    }}
                >
                    {explanation}
                </p>
            ) : (
                <p style={{ margin: 0, fontSize: '13px', color: '#999' }}>
                    Nenhuma descrição disponível.
                </p>
            )}
        </div>
    );

    if (isTabletOrMobile) {
        return (
            <div style={{ padding: '16px 20px' }}>
                <ProductImageBox image={image} loading={loading} mobile />
                {textBlock}
            </div>
        );
    }

    return (
        <div
            style={{
                padding: '16px 20px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '20px',
                alignItems: 'flex-start',
            }}
        >
            <ProductImageBox image={image} loading={loading} />
            {textBlock}
        </div>
    );
};

// ─── Aba: Especificações Técnicas ──────────────────────────────────────────────
const TechnicalSpecification = ({ subCategory }) => {
    const info = subCategory.technical_information?.trim();

    return (
        <div style={{ padding: '16px 20px' }}>
            <h4
                style={{
                    margin: '0 0 12px',
                    fontSize: '13px',
                    fontWeight: '700',
                    color: '#444',
                    textTransform: 'uppercase',
                    letterSpacing: '0.4px',
                }}
            >
                Especificações técnicas
            </h4>
            {info ? (
                <p
                    style={{
                        margin: 0,
                        fontSize: '13px',
                        color: '#444',
                        lineHeight: '1.9',
                        whiteSpace: 'pre-line',
                        maxHeight: '400px',
                        overflowY: 'auto',
                        paddingRight: '4px',
                    }}
                >
                    {info}
                </p>
            ) : (
                <p style={{ margin: 0, fontSize: '13px', color: '#999' }}>
                    Nenhuma especificação disponível.
                </p>
            )}
        </div>
    );
};

// ─── Aba: Arquivos ─────────────────────────────────────────────────────────────
const FilesList = ({ subCategory }) => {
    const [loading, setLoading] = useState(true);
    const [guides, setGuides] = useState([]);

    useEffect(() => {
        (async () => {
            const g = await getProductsGuides([subCategory.selectedModel]);
            setGuides([...g]);
            setLoading(false);
        })();
    }, []);

    const allGuides = guides.flatMap((file) =>
        file.guides.map((guide) => ({ productCode: file.product_code, guide }))
    );

    if (loading) {
        return (
            <div style={{ padding: '24px', textAlign: 'center' }}>
                <Spinner />
            </div>
        );
    }

    return (
        <div style={{ padding: '16px 20px' }}>
            <h4
                style={{
                    margin: '0 0 14px',
                    fontSize: '13px',
                    fontWeight: '700',
                    color: '#444',
                    textTransform: 'uppercase',
                    letterSpacing: '0.4px',
                }}
            >
                Arquivos disponíveis
            </h4>

            {allGuides.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <BsFilePdf
                        style={{ fontSize: '36px', color: '#ccc', marginBottom: '8px' }}
                    />
                    <p style={{ margin: 0, fontSize: '13px', color: '#999' }}>
                        Nenhum arquivo disponível.
                    </p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                    {allGuides.map(({ productCode, guide }, idx) => (
                        <div
                            key={idx}
                            onClick={() => downloadPDF(getPdfDownloadUrl(productCode, guide))}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                width: '120px',
                                padding: '12px 8px',
                                borderRadius: '8px',
                                border: '1px solid #e8e8e8',
                                backgroundColor: '#fff',
                                cursor: 'pointer',
                                transition: 'box-shadow 0.15s, border-color 0.15s',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                                e.currentTarget.style.borderColor = '#a8d48a';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow = 'none';
                                e.currentTarget.style.borderColor = '#e8e8e8';
                            }}
                        >
                            <BsFilePdf
                                style={{
                                    fontSize: '36px',
                                    color: '#e53935',
                                    marginBottom: '8px',
                                }}
                            />
                            <span
                                style={{
                                    fontSize: '11px',
                                    color: '#555',
                                    textAlign: 'center',
                                    lineHeight: '1.4',
                                    wordBreak: 'break-word',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                }}
                                title={getLastFromSplit(guide, '_')}
                            >
                                {getLastFromSplit(guide, '_')}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ─── Componente principal ──────────────────────────────────────────────────────
export const DescriptionTile = ({
    width,
    showTabOrder,
    setShowTabOrder,
    subCategory,
}) => {
    return (
        <Panel
            width={width ?? '70%'}
            style={{ display: 'block', boxSizing: 'border-box' }}
        >
            {/* Abas */}
            <Tab>
                {TABS.map((tab) => (
                    <TabItem
                        key={tab.id}
                        active={showTabOrder === tab.id}
                        onClick={() => setShowTabOrder(tab.id)}
                    >
                        {tab.label}
                    </TabItem>
                ))}
            </Tab>

            {/* Conteúdo */}
            <div>
                {showTabOrder === 0 && (
                    <EquipmentDescription subCategory={subCategory} />
                )}
                {showTabOrder === 1 && (
                    <TechnicalSpecification subCategory={subCategory} />
                )}
                {showTabOrder === 2 && <FilesList subCategory={subCategory} />}
            </div>
        </Panel>
    );
};
